import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Camera } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { VerifiedBadge } from './VerifiedBadge';
import { StarRating } from './StarRating';
import { formatDistance, formatPriceFrom } from '@/lib/studioAccent';
import { PHOTOGRAPHERS_PAGE } from '@/constants/testIds';

const FALLBACK_COVER = 'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70';

/** Directory result. Keeps LXRA gold for hover/CTA — no studio accent here. */
export const PhotographerCard = ({ studio, index = 0 }) => {
  const price = formatPriceFrom(studio.price_from_cents);
  const distance = formatDistance(studio.distance_miles);
  const styles = (studio.styles || []).slice(0, 3);
  return (
    <Reveal delay={(index % 6) * 0.05}>
      <article
        data-testid={PHOTOGRAPHERS_PAGE.card(studio.handle)}
        className="group h-full flex flex-col overflow-hidden rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] transition-[border-color,box-shadow] duration-200 hover:border-[hsl(var(--lxra-gold))]/40 hover:shadow-[0_18px_40px_-24px_rgba(201,164,92,0.35)] focus-within:border-[hsl(var(--lxra-gold))]/60"
      >
        <Link to={`/p/${studio.handle}`} className="relative block aspect-[4/3] overflow-hidden bg-[#141416]" aria-label={`View ${studio.name}`}>
          <img src={studio.cover_url || FALLBACK_COVER} alt="" loading="lazy" className="h-full w-full object-cover hover-zoom-img" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-transparent to-transparent" />
          <div className="absolute top-3 left-3 h-10 w-10 rounded-full border border-white/15 bg-black/60 backdrop-blur flex items-center justify-center overflow-hidden">
            {studio.logo_url ? <img src={studio.logo_url} alt="" className="h-full w-full object-cover" /> : <Camera className="h-4 w-4 text-[hsl(var(--lxra-gold))]" />}
          </div>
          {studio.verified && <VerifiedBadge testId={PHOTOGRAPHERS_PAGE.cardVerified(studio.handle)} className="absolute top-3 right-3" />}
        </Link>
        <div className="flex flex-1 flex-col p-5">
          <h3 className="font-display text-2xl tracking-[0.06em] text-[hsl(var(--lxra-fg))] leading-none">{studio.name}</h3>
          {studio.rating_count > 0 && (
            <span data-testid={PHOTOGRAPHERS_PAGE.cardRating(studio.handle)} className="mt-2 inline-flex items-center gap-1.5 text-xs text-[hsl(var(--lxra-fg))]">
              <StarRating value={studio.rating_average} size="h-3.5 w-3.5" />
              <span className="lxra-mono">{studio.rating_average}</span>
              <span className="text-[hsl(var(--lxra-muted))]">({studio.rating_count})</span>
            </span>
          )}
          {studio.tagline && <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed line-clamp-2">{studio.tagline}</p>}
          {styles.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {styles.map((s) => (
                <span key={s} className="rounded-full border border-[hsl(var(--lxra-border))] bg-black/20 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-fg))]">{s}</span>
              ))}
            </div>
          )}
          <div className="mt-4 flex items-center justify-between gap-3 text-xs text-[hsl(var(--lxra-muted))]">
            <span className="inline-flex items-center gap-1.5 truncate">
              <MapPin className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))] flex-shrink-0" />
              <span className="truncate">{studio.service_area || studio.home_base?.label || 'DMV'}</span>
            </span>
            {distance && <span data-testid={PHOTOGRAPHERS_PAGE.cardDistance(studio.handle)} className="flex-shrink-0 lxra-mono">{distance}</span>}
          </div>
          <div className="mt-auto pt-5 flex items-center justify-between gap-3">
            <p data-testid={PHOTOGRAPHERS_PAGE.cardPrice(studio.handle)} className="text-sm text-[hsl(var(--lxra-fg))]">
              {price ? <><span className="text-[hsl(var(--lxra-muted))] text-xs uppercase tracking-[0.1em] mr-1.5">From</span>{price}</> : <span className="text-xs text-[hsl(var(--lxra-muted))]">Pricing on request</span>}
            </p>
            <div className="flex items-center gap-2">
              <Link
                to={`/p/${studio.handle}`}
                data-testid={PHOTOGRAPHERS_PAGE.cardView(studio.handle)}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg border border-[hsl(var(--lxra-border))] text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors"
              >
                Profile
              </Link>
              <Link
                to={`/book?photographer=${studio.handle}`}
                data-testid={PHOTOGRAPHERS_PAGE.cardRequest(studio.handle)}
                className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-[11px] uppercase tracking-[0.1em] font-semibold transition-colors"
              >
                Book <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </article>
    </Reveal>
  );
};
